import React, { useState, useEffect, useRef } from 'react';
import { TbFidgetSpinner } from "react-icons/tb";
import RoleBasedHeader from '../../../components/ui/RoleBasedHeader';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const terms = [
    { value: 'first', label: 'First Term' },
    { value: 'second', label: 'Second Term' },
    { value: 'third', label: 'Third Term' }
];

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL; // Use deployed backend URL

const DEFAULT_SUBJECTS = ['Mathematics', 'English', 'Biology', 'Physics', 'Chemistry'];
const SUBJECTS_KEY = (teacherData, selectedTerm) =>
  `resultPortal_subjects_${teacherData?.className || ''}_${selectedTerm || ''}`;

const getDefaultSubjects = (className) => {
  if (/jss1|jss2|jss3/i.test(className || '')) {
    return ['Mathematics', 'English'];
  }
  return ['Mathematics', 'English', 'Biology', 'Physics', 'Chemistry'];
};

// Add this helper function before your ResultPortal component:
const autoGradeAndRemark = (score) => {
  let grade = '';
  let remark = '';
  const n = Number(score);
  if (score === '' || score === null || score === undefined || isNaN(n)) {
    return { grade: '', remark: '' };
  }
  if (n >= 80 && n <= 100) {
    grade = 'A1';
    remark = 'Excellent';
  } else if (n >= 70 && n <= 79) {
    grade = 'B2';
    remark = 'Good';
  } else if (n >= 65 && n <= 69) {
    grade = 'B3';
    remark = 'Good';
  } else if (n >= 60 && n <= 64) {
    grade = 'C4';
    remark = 'Good';
  } else if (n >= 55 && n <= 59) {
    grade = 'C5';
    remark = 'Good';
  } else if (n >= 50 && n <= 54) {
    grade = 'C6';
    remark = 'Good';
  } else if (n >= 45 && n <= 49) {
    grade = 'D7';
    remark = 'Pass';
  } else if (n >= 40 && n <= 44) {
    grade = 'E8';
    remark = 'Pass';
  } else if (n >= 0 && n <= 39) {
    grade = 'F9';
    remark = 'Fail';
  }
  return { grade, remark };
};

const ResultPortal = () => {
    const [teacherData, setTeacherData] = useState(null);
    const [selectedTerm, setSelectedTerm] = useState('');
    const [students, setStudents] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [newSubject, setNewSubject] = useState('');
    const [results, setResults] = useState({});
    const [overall, setOverall] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [teacherResults, setTeacherResults] = useState([]);
    const [isLoadingStudents, setIsLoadingStudents] = useState(false);
    const [isLoadingResults, setIsLoadingResults] = useState(false);
    const [manualOverall, setManualOverall] = useState({});
    const [caScores, setCaScores] = useState({});
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Confirmation modals
    const [showGeneralPushConfirm, setShowGeneralPushConfirm] = useState(false);
    const [showIndividualPushConfirm, setShowIndividualPushConfirm] = useState(false);
    const [pendingIndividualPushId, setPendingIndividualPushId] = useState(null);


    // Pushed state tracking
    const [pushedMap, setPushedMap] = useState({}); // { [studentId]: true/false }
    const [initialStudentData, setInitialStudentData] = useState({}); // for change detection
    const [hasChanged, setHasChanged] = useState({}); // { [studentId]: true/false }

    // 1. Insert this function after your state declarations (after all useState calls):
    const buildSaveAllPayload = (studentId) => {
      const payload = {
        studentId: studentId,
        teacherId: teacherData._id,
        className: teacherData.className,
        term: selectedTerm,
        subjects: subjects,
        caScores: caScores[studentId] || {},
        ...overall[studentId],
      };

      // Ensure absent is set as (nil) if 0
      if ('absent' in payload) {
        if (payload.absent === 0 || payload.absent === '0') {
          payload.absent = '(nil)';
        }
      }

      // Only include result fields for subjects currently in the tray
      subjects.forEach(subject => {
        payload[`${subject}_project`] = results[studentId]?.[`${subject}_project`] ?? '';
        payload[`${subject}_cat1`] = results[studentId]?.[`${subject}_cat1`] ?? '';
        payload[`${subject}_cat2`] = results[studentId]?.[`${subject}_cat2`] ?? '';
        payload[`${subject}_exam`] = results[studentId]?.[`${subject}_exam`] ?? '';
        payload[`${subject}_maxScore`] = results[studentId]?.[`${subject}_maxScore`] !== undefined && results[studentId]?.[`${subject}_maxScore`] !== null
    ? String(results[studentId][`${subject}_maxScore`])
    : '';
        payload[`${subject}_minScore`] = results[studentId]?.[`${subject}_minScore`] !== undefined && results[studentId]?.[`${subject}_minScore`] !== null
    ? String(results[studentId][`${subject}_minScore`])
    : '';
        payload[`${subject}_grade`] = results[studentId]?.[`${subject}_grade`] ?? '';
        payload[`${subject}_remark`] = results[studentId]?.[`${subject}_remark`] ?? '';
      });

      // Remove any stray subject fields from previous results (defensive)
      Object.keys(payload).forEach(key => {
        // If the key is a subject field but not for a subject in the tray, remove it
        const match = key.match(/^(.*)_(project|cat1|cat2|exam|maxScore|minScore|grade|remark)$/);
        if (match && !subjects.includes(match[1])) {
          delete payload[key];
        }
      });

      return payload;
    };

    // Get teacher data from localStorage
    useEffect(() => {
        const stored = localStorage.getItem('teacherData');
        if (stored) setTeacherData(JSON.parse(stored));
    }, []);

    // Fetch all students registered under this class (not just those who took the exam)
    useEffect(() => {
        if (teacherData?.className && selectedTerm) {
            setIsLoadingStudents(true);
            fetch(`${API_BASE_URL}/api/user/class/${encodeURIComponent(teacherData.className)}/${encodeURIComponent(selectedTerm)}/students`, {
                credentials: 'include'
            })
                .then(res => res.json())
                .then(data => setStudents(Array.isArray(data) ? data : []))
                .catch(() => setStudents([]))
                .finally(() => setIsLoadingStudents(false));
        }
    }, [teacherData, selectedTerm]);

    // Fetch teacher's results for the class and term
    useEffect(() => {
        if (teacherData?.className && selectedTerm) {
            setIsLoadingResults(true);
            fetch(`${API_BASE_URL}/api/results/class/${encodeURIComponent(teacherData.className)}/${encodeURIComponent(selectedTerm)}`, {
                credentials: 'include'
            })
                .then(res => res.json())
                .then(data => setTeacherResults(Array.isArray(data) ? data : []))
                .catch(() => setTeacherResults([]))
                .finally(() => setIsLoadingResults(false));
        }
    }, [teacherData, selectedTerm]);

    // Map fetched results to local state and track pushed status
    useEffect(() => {
        if (teacherResults.length && students.length && subjects.length) {
            const newResults = { ...results };
            const newCaScores = { ...caScores };
            const newPushedMap = { ...pushedMap };
            const newInitialStudentData = { ...initialStudentData };
            students.forEach(student => {
                newResults[student._id] = { ...(results[student._id] || {}) };
                newCaScores[student._id] = { ...(caScores[student._id] || {}) };
                const studentResults = teacherResults.filter(r => r.studentId === student._id);
                const pushed = studentResults.some(r => r.pushed === true);
                newPushedMap[student._id] = pushed;
                // Save initial data for change detection
                const overallResult = studentResults.find(r => r.subject === 'Overall') || {};
                newInitialStudentData[student._id] = {
                  results: { ...newResults[student._id] },
                  caScores: { ...newCaScores[student._id] },
                  overall: { ...overall[student._id], ...overallResult }
                };
                subjects.forEach(subject => {
                    // Only update if found in teacherResults AND subject is in tray
                    const foundExam = studentResults.find(
                        r => r.subject === subject
                    );
                    if (foundExam && foundExam.score !== undefined && foundExam.score !== null) {
                        newResults[student._id][subject] = foundExam.score;
                        // Map all subject fields
                        newResults[student._id][`${subject}_project`] = foundExam.projectScore ?? '';
                        newResults[student._id][`${subject}_cat1`] = foundExam.firstCatScore ?? '';
                        newResults[student._id][`${subject}_cat2`] = foundExam.secondCatScore ?? '';
                        newResults[student._id][`${subject}_exam`] = foundExam.examScore ?? '';
                        newResults[student._id][`${subject}_maxScore`] = foundExam.maxScore ?? '';
                        newResults[student._id][`${subject}_minScore`] = foundExam.minScore ?? '';
                        newResults[student._id][`${subject}_grade`] = foundExam.grade ?? '';
                        newResults[student._id][`${subject}_remark`] = foundExam.remark ?? '';
                    }
                    // C/A score
                    const foundCA = studentResults.find(
                        r => r.subject === `${subject} C/A`
                    );
                    if (foundCA && foundCA.score !== undefined && foundCA.score !== null) {
                        newCaScores[student._id][subject] = foundCA.score;
                    }
                });
            });
            setResults(newResults);
            setCaScores(newCaScores);
            setPushedMap(newPushedMap);
            setInitialStudentData(newInitialStudentData);

            // Map overall score, header, remarks, key ratings, etc.
            const newOverall = {};
            students.forEach(student => {
                // Always use the backend "Overall" result as source of truth
                const found = teacherResults.find(
                    r => r.studentId === student._id && r.subject === 'Overall'
                );
                if (found) {
                    newOverall[student._id] = { ...found };
                } else {
                    // If not found, preserve any local unsaved edits
                    newOverall[student._id] = overall[student._id] || {};
                }
            });
            setOverall(newOverall);
        }
    // eslint-disable-next-line
    }, [teacherResults, students, subjects]);

    // Merge backend subjects into tray ONLY if tray is empty (first load, e.g. on a new device)
useEffect(() => {
  if (
    teacherResults.length &&
    students.length &&
    teacherData?.className &&
    selectedTerm
  ) {
    const key = SUBJECTS_KEY(teacherData, selectedTerm);
    const savedSubjects = localStorage.getItem(key);
    let currentSubjects = [];
    if (savedSubjects) {
      try {
        const parsed = JSON.parse(savedSubjects);
        if (Array.isArray(parsed)) currentSubjects = parsed;
      } catch {}
    }

    // Find all subjects that have been soft-deleted for any student in this class/term
    const softDeletedSubjects = new Set(
      teacherResults
        .filter(
          (r) =>
            r.subject &&
            r.subject !== "Overall" &&
            !r.subject.endsWith("C/A") &&
            r.deleted === true
        )
        .map((r) => r.subject)
    );

    const forbiddenSubjects = /jss1|jss2|jss3/i.test(teacherData.className || '')
      ? new Set(['Chemistry', 'Physics', 'Biology'])
      : new Set();

    // Only merge backend subjects that are present in teacherResults and NOT soft-deleted for any student
    const backendSubjects = Array.from(
      new Set(
        teacherResults
          .filter(
            (r) =>
              r.subject &&
              r.subject !== "Overall" &&
              !r.subject.endsWith("C/A") &&
              r.deleted !== true &&
              !softDeletedSubjects.has(r.subject) &&
              !forbiddenSubjects.has(r.subject) // <-- filter out for JSS1-3
          )
          .map((r) => r.subject)
      )
    );
    // Only add backend subjects that are not already in the tray
    const missingSubjects = backendSubjects.filter(s => !currentSubjects.includes(s));
    if (missingSubjects.length > 0) {
      const mergedSubjects = [...currentSubjects, ...missingSubjects];
      setSubjects(mergedSubjects);
      localStorage.setItem(key, JSON.stringify(mergedSubjects));
    }
    // Remove any subject from the tray that is NOT in backendSubjects (to keep tray in sync)
    const filteredSubjects = currentSubjects.filter(s => backendSubjects.includes(s));
    if (filteredSubjects.length !== currentSubjects.length) {
      setSubjects(filteredSubjects);
      localStorage.setItem(key, JSON.stringify(filteredSubjects));
    }
  }
  // eslint-disable-next-line
}, [teacherResults, students, teacherData, selectedTerm]);

// Load subjects from localStorage (or fallback to default ONLY if tray is empty)
useEffect(() => {
  if (teacherData?.className && selectedTerm) {
    const key = SUBJECTS_KEY(teacherData, selectedTerm);
    const saved = localStorage.getItem(key);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSubjects(parsed);
          return;
        }
      } catch {}
    }
    // Use dynamic default subjects based on class
    const defaults = getDefaultSubjects(teacherData.className);
    setSubjects(defaults);
    localStorage.setItem(key, JSON.stringify(defaults));
  }
  // eslint-disable-next-line
}, [teacherData, selectedTerm]);

// Save subjects to localStorage whenever they change
useEffect(() => {
  if (teacherData?.className && selectedTerm) {
    const key = SUBJECTS_KEY(teacherData, selectedTerm);
    localStorage.setItem(key, JSON.stringify(subjects));
  }
}, [subjects, teacherData, selectedTerm]);

    // When mapping backend results, NEVER restore deleted subjects
    useEffect(() => {
      if (teacherResults.length && students.length) {
        // Only add subjects that are not in the current subjects list AND not in the original default subjects if deleted
        const currentSubjects = new Set(subjects);
        let changed = false;
        teacherResults.forEach(r => {
          if (
            r.subject &&
            r.subject !== 'Overall' &&
            r.deleted === true &&
            !currentSubjects.has(r.subject)
          ) {
          }
        });
      }
    }, [teacherResults, students]);

    // Automatically update overall score and position when subject scores change, unless manually edited
    // ...existing code...

useEffect(() => {
    if (students.length && subjects.length) {
        const newOverall = { ...overall };
        const scoresArr = students.map(student => {
            const studentId = student._id;
            const subjectScores = subjects.map(subject => {
                const ca = Number(caScores?.[studentId]?.[subject] || 0);
                const exam = Number(results?.[studentId]?.[subject] || 0);
                let total = ca + exam;
                if (total < 0) total = 0;
                if (total > 100) total = 100;
                return total;
            });
            const autoScore = subjectScores.length
                ? Math.round(subjectScores.reduce((a, b) => a + b, 0) / subjectScores.length)
                : 0;
            return {
                studentId,
                autoScore,
            };
        });
        const sorted = [...scoresArr].sort((a, b) => b.autoScore - a.autoScore);
        const positionMap = {};
        let lastScore = null;
        let lastPosition = 0;
        let actualPosition = 1;
        sorted.forEach((item, idx) => {
            if (lastScore !== null && item.autoScore === lastScore) {
                positionMap[item.studentId] = lastPosition;
            } else {
                positionMap[item.studentId] = actualPosition;
                lastScore = item.autoScore;
                lastPosition = actualPosition;
            }
            actualPosition++;
        });
        students.forEach(student => {
            const studentId = student._id;
            const autoScore = scoresArr.find(s => s.studentId === studentId)?.autoScore || 0;
            const autoPosition = positionMap[studentId];

            // Only set auto values if the field is truly undefined (not empty string, not 0, not any value)
            // This ensures any keyed/saved value (including 0 or large numbers) is preserved
            if (
                newOverall[studentId]?.overallScore === undefined ||
                newOverall[studentId]?.overallScore === null
            ) {
                newOverall[studentId] = {
                    ...(newOverall[studentId] || {}),
                    overallScore: autoScore,
                    position: autoPosition ? `${autoPosition}${autoPosition === 1 ? 'st' : autoPosition === 2 ? 'nd' : autoPosition === 3 ? 'rd' : 'th'}` : ''
                };
            } else {
                // Always update position if not manually set, but never touch overallScore
                newOverall[studentId] = {
                    ...(newOverall[studentId] || {}),
                    position: newOverall[studentId]?.position || (autoPosition ? `${autoPosition}${autoPosition === 1 ? 'st' : autoPosition === 2 ? 'nd' : autoPosition === 3 ? 'rd' : 'th'}` : ''),
                    overallScore: newOverall[studentId]?.overallScore
                };
            }
        });
        setOverall(newOverall);
    }
// eslint-disable-next-line
}, [results, caScores, students, subjects]);

    // Add new subject to tray
    const handleAddSubject = () => {
      const subject = newSubject.trim();
      if (subject && !subjects.includes(subject)) {
        setSubjects(prevSubjects => [...prevSubjects, subject]);
        setNewSubject('');
        setResults(prevResults => {
          if (!students || !students.length) return prevResults;
          const updatedResults = { ...prevResults };
          students.forEach(student => {
            updatedResults[student._id] = { ...(prevResults[student._id] || {}) };
            [
              `${subject}_project`,
              `${subject}_cat1`,
              `${subject}_cat2`,
              `${subject}_exam`,
              `${subject}_maxScore`,
              `${subject}_minScore`,
              `${subject}_grade`,
              `${subject}_remark`,
              `${subject}_overallScore`
            ].forEach(field => {
              if (!(field in updatedResults[student._id])) {
                updatedResults[student._id][field] = '';
              }
            });
          });
          return updatedResults;
        });
        setCaScores(prevCaScores => {
          if (!students || !students.length) return prevCaScores;
          const updatedCaScores = { ...prevCaScores };
          students.forEach(student => {
            updatedCaScores[student._id] = { ...(prevCaScores[student._id] || {}) };
            if (!(subject in updatedCaScores[student._id])) {
              updatedCaScores[student._id][subject] = '';
            }
          });
          return updatedCaScores;
        });
        // Save to localStorage for the whole class/term
        if (teacherData?.className && selectedTerm) {
          const key = SUBJECTS_KEY(teacherData, selectedTerm);
          localStorage.setItem(key, JSON.stringify([...subjects, subject]));
        }
      }
    };

    // Remove subject from tray
    const handleRemoveSubject = async (subject) => {
  // Call backend to soft-delete for all students in this class/term
  if (teacherData?.className && selectedTerm) {
    await fetch(`${API_BASE_URL}/api/results/delete-subject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        className: teacherData.className,
        term: selectedTerm,
        subject,
        // Remove studentId to delete for all students
      }),
    });
  }

  const updatedSubjects = subjects.filter(s => s !== subject);
  setSubjects(updatedSubjects);

  // Remove subject fields from results and caScores for all students
  setResults(prevResults => {
    const updatedResults = { ...prevResults };
    students.forEach(student => {
      if (updatedResults[student._id]) {
        [
          `${subject}_project`,
          `${subject}_cat1`,
          `${subject}_cat2`,
          `${subject}_exam`,
          `${subject}_maxScore`,
          `${subject}_minScore`,
          `${subject}_grade`,
          `${subject}_remark`,
          subject // the main score field
        ].forEach(field => {
          delete updatedResults[student._id][field];
        });
      }
    });
    return updatedResults;
  });

  setCaScores(prevCaScores => {
    const updatedCaScores = { ...prevCaScores };
    students.forEach(student => {
      if (updatedCaScores[student._id]) {
        delete updatedCaScores[student._id][subject];
      }
    });
    return updatedCaScores;
  });

  // Save updated subjects to localStorage for the whole class/term
  if (teacherData?.className && selectedTerm) {
    const key = SUBJECTS_KEY(teacherData, selectedTerm);
    localStorage.setItem(key, JSON.stringify(updatedSubjects));
  }
};


    const keyRatingFields = [
      'handwriting', 'punctuality', 'initiative', 'obedience', 'honesty',
      'neatness', 'attentiveness', 'respect', 'attitude', 'creativity'
    ];

    // Handle result input change
    const handleResultChange = (studentId, field, value) => {
      setResults(prev => {
        const updated = {
          ...prev,
          [studentId]: {
            ...(prev[studentId] || {}),
            [field]: keyRatingFields.includes(field) && value !== '' ? Number(value) : value
          }
        };

        // Auto-calculate grade and remark for subject total
        // Only if the field is a subject score (project/cat1/cat2/exam), recalculate total, grade, remark
        const subjectMatch = field.match(/^(.*)_(project|cat1|cat2|exam)$/);
        if (subjectMatch) {
          const subject = subjectMatch[1];
          const project = Number(updated[studentId][`${subject}_project`] || 0);
          const cat1 = Number(updated[studentId][`${subject}_cat1`] || 0);
          const cat2 = Number(updated[studentId][`${subject}_cat2`] || 0);
          const exam = Number(updated[studentId][`${subject}_exam`] || 0);
          const total = project + cat1 + cat2 + exam;
          updated[studentId][subject] = total;

          // Auto grade and remark
          const { grade, remark } = autoGradeAndRemark(total);
          updated[studentId][`${subject}_grade`] = grade;
          updated[studentId][`${subject}_remark`] = remark;
        }
        return updated;
      });
      setHasChanged(prev => ({ ...prev, [studentId]: true }));
    };

    // Handle overall score and position change (manual edit)
    const handleOverallChange = (studentId, field, value) => {
  setOverall(prev => ({
    ...prev,
    [studentId]: {
      ...(prev[studentId] || {}),
      [field]:
        field === 'absent'
          ? (value === '' ? '' : (value === '0' || value === 0 ? '(nil)' : Number(value)))
          : value
    }
  }));
  if (field === 'overallScore') {
    setManualOverall(prev => ({ ...prev, [studentId]: true }));
  }
  setHasChanged(prev => ({ ...prev, [studentId]: true }));
};

    // Detect changes for Save/Push button enable/disable
    useEffect(() => {
      if (!selectedStudent) return;
      const studentId = selectedStudent._id;
      const initial = initialStudentData[studentId];
      if (!initial) return;
      const isChanged =
        JSON.stringify(results[studentId] || {}) !== JSON.stringify(initial.results || {}) ||
        JSON.stringify(caScores[studentId] || {}) !== JSON.stringify(initial.caScores || {}) ||
        JSON.stringify(overall[studentId] || {}) !== JSON.stringify(initial.overall || {});
      setHasChanged(prev => ({ ...prev, [studentId]: isChanged }));
    }, [results, caScores, overall, selectedStudent, initialStudentData]);

    // Helper: Check if a student has at least one subject or overall result filled
    const getStudentsWithEmptyResults = () => {
      return students.filter(student => {
        // Check if at least one subject or overall score is filled
        const hasSubjectScore = subjects.some(
          subject =>
            results?.[student._id]?.[subject] !== undefined &&
            results?.[student._id]?.[subject] !== '' &&
            results?.[student._id]?.[subject] !== null
        );
        const hasOverall = overall?.[student._id]?.overallScore !== undefined &&
          overall?.[student._id]?.overallScore !== '' &&
          overall?.[student._id]?.overallScore !== null;
        return !hasSubjectScore && !hasOverall;
      });
    };

    // Submit results for all students (Push All)
    const handleSubmitResults = async () => {
        setIsSubmitting(true);
        setSuccessMsg('');
        setErrorMsg('');

        // Check for students with empty results
        const emptyStudents = getStudentsWithEmptyResults();
        if (emptyStudents.length > 0) {
          setErrorMsg(
            `Cannot push results. The following students have no result data entered: ${emptyStudents
              .map(s => s.name || s._id)
              .join(', ')}. Please enter at least one subject or overall score for each student before pushing.`
          );
          setIsSubmitting(false);
          return;
        }

        let allSuccess = true;
        let failedStudents = [];

        try {
            for (const student of students) {
                const payload = buildSaveAllPayload(student._id);
                payload.pushed = true;
                console.log('Payload being pushed:', payload);
                const res = await fetch(`${API_BASE_URL}/api/results/save-all`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                });
                const data = await res.json();
                if (!data.success) {
                    allSuccess = false;
                    failedStudents.push(student.name || student._id);
                }
            }
            if (allSuccess) {
                setSuccessMsg('Results have been successfully pushed to students.');
            } else {
                setErrorMsg(`Failed to push results for: ${failedStudents.join(', ')}. Please try again.`);
            }
        } catch (err) {
            setErrorMsg('Failed to push results. Please try again.');
        }
        setIsSubmitting(false);
        setTimeout(() => {
            fetch(`${API_BASE_URL}/api/results/class/${encodeURIComponent(teacherData.className)}/${encodeURIComponent(selectedTerm)}`, {
              credentials: 'include'
            })
              .then(res => res.json())
              .then(data => setTeacherResults(Array.isArray(data) ? data : []));
        }, 500);
    };

    // Push individual student result
   // ...existing code...

// Update handlePushIndividualResult to only push/save the selected student's result (no global max/min logic)
const handlePushIndividualResult = async (studentId) => {
  setIsSubmitting(true);
  setSuccessMsg('');
  setErrorMsg('');
  try {
    // Only push for the selected student (no global max/min logic)
    const payload = buildSaveAllPayload(studentId);
    payload.pushed = true;
    const res = await fetch(`${API_BASE_URL}/api/results/save-all`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json();

    if (data.success) {
      setSuccessMsg('Student result has been successfully updated.');
    } else {
      setErrorMsg('Failed to update student result. Please try again.');
    }
  } catch (err) {
    setErrorMsg('Failed to update student result. Please try again.');
  }
  setIsSubmitting(false);
  // Refetch results to update pushedMap
  setTimeout(() => {
    fetch(`${API_BASE_URL}/api/results/class/${encodeURIComponent(teacherData.className)}/${encodeURIComponent(selectedTerm)}`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setTeacherResults(Array.isArray(data) ? data : []));
  }, 500);
};
// ...existing code...

    // Load saved result data for selected student
    useEffect(() => {
      if (selectedStudent) {
        const key = `resultPortal_${teacherData?.className}_${selectedTerm}_${selectedStudent._id}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          setResults(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.results || {},
          }));
          setCaScores(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.caScores || {},
          }));
          setOverall(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.overall || {},
          }));
        }
      }
    }, [selectedStudent, teacherData, selectedTerm]);

    // Load saved result data for selected student from backend first, fallback to localStorage
   // Load saved result data for selected student from backend first, fallback to localStorage
useEffect(() => {
  const fetchStudentResults = async () => {
    if (selectedStudent && teacherData?.className && selectedTerm) {
      try {
        // Try to fetch from backend
        const res = await fetch(
          `${API_BASE_URL}/api/results/student/${selectedStudent._id}/${selectedTerm}/${teacherData.className}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          // 1. Always use subjects from localStorage (the tray) as the source of truth
          const key = SUBJECTS_KEY(teacherData, selectedTerm);
          const savedSubjects = localStorage.getItem(key);
          let currentSubjects = DEFAULT_SUBJECTS;
          if (savedSubjects) {
            try {
              const parsed = JSON.parse(savedSubjects);
              if (Array.isArray(parsed)) currentSubjects = parsed;
            } catch {}
          }
          setSubjects(currentSubjects);

          // 2. Map backend data to local state (all fields)
          const resultObj = {};
          const caObj = {};
          let overallObj = {};
          data.forEach(r => {
            if (r.subject === 'Overall') {
              overallObj = { ...r };
              // Extract key rating, header, and remark fields from 'Overall'
              [
                'handwriting', 'punctuality', 'initiative', 'obedience', 'honesty',
                'neatness', 'attentiveness', 'respect', 'attitude', 'creativity',
                'teacherComment', 'signatureDate', 'headTeacherRemark',
                'academicYear', 'schoolOpened', 'present', 'absent',
                'totalScore', 'projectDefence', 'overallScore', 'average', 'nextTerm'
              ].forEach(field => {
                if (r[field] !== undefined && r[field] !== null) {
                  resultObj[field] = r[field];
                }
              });
            } else if (r.subject.endsWith('C/A')) {
              const subj = r.subject.replace(' C/A', '');
              caObj[subj] = r.score;
            } else {
              // Map all subject fields
              resultObj[`${r.subject}_project`] = r.projectScore ?? '';
              resultObj[`${r.subject}_cat1`] = r.firstCatScore ?? '';
              resultObj[`${r.subject}_cat2`] = r.secondCatScore ?? '';
              resultObj[`${r.subject}_exam`] = r.examScore ?? '';
              resultObj[`${r.subject}_maxScore`] = r.maxScore ?? '';
              resultObj[`${r.subject}_minScore`] = r.minScore ?? '';
              resultObj[`${r.subject}_grade`] = r.grade ?? '';
              resultObj[`${r.subject}_remark`] = r.remark ?? '';
              resultObj[r.subject] = r.score ?? '';
            }
          });
          setResults(prev => ({
            ...prev,
            [selectedStudent._id]: resultObj,
          }));
          setCaScores(prev => ({
            ...prev,
            [selectedStudent._id]: caObj,
          }));
          setOverall(prev => ({
            ...prev,
            [selectedStudent._id]: overallObj,
          }));
        } else {
          // Fallback to localStorage for unsaved drafts
          const key = `resultPortal_${teacherData?.className}_${selectedTerm}_${selectedStudent._id}`;
          const saved = localStorage.getItem(key);
          if (saved) {
            const parsed = JSON.parse(saved);
            setSubjects(parsed.subjects || DEFAULT_SUBJECTS);
            setResults(prev => ({
              ...prev,
              [selectedStudent._id]: parsed.results || {},
            }));
            setCaScores(prev => ({
              ...prev,
              [selectedStudent._id]: parsed.caScores || {},
            }));
            setOverall(prev => ({
              ...prev,
              [selectedStudent._id]: parsed.overall || {},
            }));
          }
        }
      } catch {
        // Fallback to localStorage if fetch fails
        const key = `resultPortal_${teacherData?.className}_${selectedTerm}_${selectedStudent._id}`;
        const saved = localStorage.getItem(key);
        if (saved) {
          const parsed = JSON.parse(saved);
          setSubjects(parsed.subjects || DEFAULT_SUBJECTS);
          setResults(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.results || {},
          }));
          setCaScores(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.caScores || {},
          }));
          setOverall(prev => ({
            ...prev,
            [selectedStudent._id]: parsed.overall || {},
          }));
        }
      }
    }
  };
  fetchStudentResults();
}, [selectedStudent, teacherData, selectedTerm]);

    // Save result data for selected student to localStorage
    useEffect(() => {
      if (selectedStudent) {
        localStorage.setItem(
          `resultPortal_${teacherData?.className}_${selectedTerm}_${selectedStudent._id}`,
          JSON.stringify({
            results: results[selectedStudent._id] || {},
            caScores: caScores[selectedStudent._id] || {},
            overall: overall[selectedStudent._id] || {},
            subjects,
          })
        );
      }
    }, [results, caScores, overall, subjects, selectedStudent, teacherData, selectedTerm]);

    // Autosave result data for selected student to backend (debounced)
    useEffect(() => {
      if (selectedStudent) {
        const timeout = setTimeout(() => {
          const payload = buildSaveAllPayload(selectedStudent._id, false);
          if (
            Object.keys(results[selectedStudent._id] || {}).length > 0 ||
            Object.keys(caScores[selectedStudent._id] || {}).length > 0 ||
            Object.keys(overall[selectedStudent._id] || {}).length > 0
          ) {
            fetch(`${API_BASE_URL}/api/results/save-all`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            });
          }
        }, 17000);
        return () => clearTimeout(timeout);
      }
    }, [results, caScores, overall, subjects, selectedStudent, teacherData, selectedTerm]);

    // Term selection before entering main page
    if (!selectedTerm) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="bg-card border border-border rounded-xl shadow-lg p-8 w-full max-w-md text-center">
                    <h2 className="font-heading text-2xl font-bold text-primary mb-4">Select Term</h2>
                    <select
                        className="w-full border rounded-lg px-3 py-2 mb-6 focus:outline-none focus:ring"
                        value={selectedTerm}
                        onChange={e => setSelectedTerm(e.target.value)}
                    >
                        <option value="">Choose a term</option>
                        {terms.map(term => (
                            <option key={term.value} value={term.value}>{term.label}</option>
                        ))}
                    </select>
                    <Button
                        variant="default"
                        disabled={!selectedTerm}
                        onClick={() => {}}
                        className="w-full"
                    >
                        <Icon name="ArrowRight" size={16} className="mr-2" />
                        Proceed
                    </Button>
                </div>
            </div>
        );
    }

    // Confirmation Modal Component
    const ConfirmationModal = ({ show, onConfirm, onCancel, message, loading }) => {
      if (!show) return null;
      return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-sm w-full border border-gray-200">
            <div className="mb-4 text-center text-lg font-semibold">{message}</div>
            <div className="flex justify-center gap-4">
              <Button variant="secondary" onClick={onCancel} disabled={loading}>
                Cancel
              </Button>
              <Button variant="primary" onClick={onConfirm} disabled={loading}>
                {loading ? <TbFidgetSpinner className="animate-spin mr-2 inline" /> : null}
                Confirm
              </Button>
            </div>
          </div>
        </div>
      );
    };

    // If a student is selected, show the result entry page for that student
    if (selectedStudent) {
        const studentId = selectedStudent._id;
        const isPushed = pushedMap[studentId];
        const isChanged = hasChanged[studentId];
        return (
            <main className="pt-20 pb-10 bg-background min-h-screen">
                <div className="max-w-3xl mx-auto px-2 sm:px-4">
                    <Button variant="outline" onClick={() => setSelectedStudent(null)} className="mb-4">
                        <Icon name="ArrowLeft" size={16} className="mr-2" />
                        Back to Student List
                    </Button>
                    <h2 className="font-heading text-2xl font-bold text-primary mb-2">
                        Enter Result for {selectedStudent.name}
                        {isPushed && (
                          <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
                            <Icon name="CheckCircle" size={14} className="mr-1" /> Pushed
                          </span>
                        )}
                    </h2>
                    <div className="bg-card border border-border rounded-xl shadow-lg p-6">
                        {(successMsg || errorMsg) && (
                          <div
                            className={`mb-4 px-4 py-2 rounded-lg font-medium text-center transition-all ${
                              successMsg
                                ? 'bg-green-100 text-green-700 border border-green-300'
                                : 'bg-red-100 text-red-700 border border-red-300'
                            }`}
                          >
                            {successMsg || errorMsg}
                            <button
                              className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                              onClick={() => {
                                setSuccessMsg('');
                                setErrorMsg('');
                              }}
                              aria-label="Dismiss"
                            >
                              &times;
                            </button>
                          </div>
                        )}
                        <form
                            onSubmit={e => {
                                e.preventDefault();
                                setPendingIndividualPushId(selectedStudent._id);
                                setShowIndividualPushConfirm(true);
                            }}
                        >
                            <div className="overflow-x-auto">
                              <table className="min-w-full border-collapse mb-6">
                                <thead>
                                  <tr>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Subject</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Project (10%)</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">1st C.A.T (20%)</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">2nd C.A.T (20%)</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Exam (50%)</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Total (100%)</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Max Score</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Min Score</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Grade</th>
                                    <th className="px-2 py-2 text-left font-semibold text-muted-foreground">Remark</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {subjects.map(subject => {
                                    // Calculate total automatically
                                    const project = Number(results?.[selectedStudent._id]?.[`${subject}_project`] || 0);
                                    const cat1 = Number(results?.[selectedStudent._id]?.[`${subject}_cat1`] || 0);
                                    const cat2 = Number(results?.[selectedStudent._id]?.[`${subject}_cat2`] || 0);
                                    const exam = Number(results?.[selectedStudent._id]?.[`${subject}_exam`] || 0);
                                    const total = project + cat1 + cat2 + exam;

                                    return (
                                      <tr key={subject}>
                                        <td className="px-2 py-2">{subject}</td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_project`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_project`, e.target.value)}
                                            placeholder="Project"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_cat1`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_cat1`, e.target.value)}
                                            placeholder="1st C.A.T"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_cat2`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_cat2`, e.target.value)}
                                            placeholder="2nd C.A.T"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_exam`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_exam`, e.target.value)}
                                            placeholder="Exam"
                                          />
                                        </td>
                                        <td className="px-2 py-2 font-bold text-primary">
                                          {total}
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_maxScore`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_maxScore`, e.target.value)}
                                            placeholder="Max"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_minScore`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_minScore`, e.target.value)}
                                            placeholder="Min"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-16 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_grade`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_grade`, e.target.value)}
                                            placeholder="Grade"
                                          />
                                        </td>
                                        <td className="px-2 py-2">
                                          <input
                                            type="text"
                                            className="w-24 border rounded-lg px-2 py-1"
                                            value={results?.[selectedStudent._id]?.[`${subject}_remark`] || ''}
                                            onChange={e => handleResultChange(selectedStudent._id, `${subject}_remark`, e.target.value)}
                                            placeholder="Remark"
                                          />
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {/* Subject Tray (Add/Remove Subjects) */}
                            <div className="mb-6">
                              <h4 className="font-semibold mb-2">Subjects</h4>
                              <div className="flex flex-wrap gap-2 mb-2">
                                {subjects.map(subject => (
                                  <span key={subject} className="flex items-center bg-gray-100 border border-gray-300 rounded px-3 py-1 text-sm font-medium">
                                    {subject}
                                    <button
                                      type="button"
                                      className="ml-2 text-red-500 hover:text-red-700"
                                      onClick={() => handleRemoveSubject(subject)}
                                      title="Remove subject"
                                    >
                                      &times;
                                    </button>
                                  </span>
                                ))}
                              </div>
                              <div className="flex gap-2">
                                <input
                                  type="text"
                                  className="border rounded px-2 py-1 w-40"
                                  value={newSubject}
                                  onChange={e => setNewSubject(e.target.value)}
                                  placeholder="Add subject"
                                />
                                <Button
                                  variant="default"
                                  type="button"
                                  onClick={handleAddSubject}
                                  disabled={!newSubject.trim()}
                                >
                                  Add
                                </Button>
                              </div>
                            </div>

                            {/* Key Rating Section - Responsive & Professional */}
                            <div className="mb-4">
                              <h4 className="font-semibold mb-2">Key Rating</h4>
                              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                                {[
                                  'handwriting', 'punctuality', 'initiative', 'obedience', 'honesty',
                                  'neatness', 'attentiveness', 'respect', 'attitude', 'creativity'
                                ].map(key => (
                                  <div key={key} className="flex flex-col items-start bg-gray-50 border border-gray-200 rounded-lg p-2">
                                    <label className="text-sm font-medium capitalize mb-1">{key}</label>
                                    <select
                                      className="border rounded px-2 py-1 w-full"
                                      value={overall?.[selectedStudent._id]?.[key] || ''}
                                      onChange={e => handleOverallChange(selectedStudent._id, key, e.target.value)}
                                    >
                                      <option value="">Not marked</option>
                                      <option value="5">5-Excellent</option>
                                      <option value="4">4-Very Good</option>
                                      <option value="3">3-Satisfactory</option>
                                      <option value="2">2-Poor</option>
                                      <option value="1">1-Very Poor</option>
                                    </select>
                                  </div>
                                ))}
                              </div>
                              <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
                                <span>5-EXCELLENT</span>
                                <span>4-VERY GOOD</span>
                                <span>3-SATISFACTORY</span>
                                <span>2-POOR</span>
                                <span>1-VERY POOR</span>
                              </div>
                            </div>

                            {/* Teacher's Comment, Signature, Head Teacher's Remark */}
                            <div className="mb-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                              <div>
                                <label className="font-semibold">Form Teacher's Comment:</label>
                                <textarea
                                  className="border rounded px-2 py-1 w-full"
                                  rows={2}
                                  value={overall?.[selectedStudent._id]?.teacherComment || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'teacherComment', e.target.value)}
                                  placeholder="Comment"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Signature & Date:</label>
                                <input
                                  type="text"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.signatureDate || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'signatureDate', e.target.value)}
                                  placeholder="Signature & Date"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Head Teacher's Remark:</label>
                                <textarea
                                    className="border rounded px-2 py-1 w-full"
                                    rows={2}
                                    value={overall?.[selectedStudent._id]?.headTeacherRemark || ''}
                                    onChange={e => handleOverallChange(selectedStudent._id, 'headTeacherRemark', e.target.value)}
                                    placeholder="Head Teacher's Remark"
                                />
                              </div>
                            </div>
                            <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                              <div>
                                <label className="font-semibold">Academic Session</label>
                                <input
                                  type="text"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.academicYear || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'academicYear', e.target.value)}
                                  placeholder="Academic Session"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">No. of Times School Opened:</label>
                                <input
                                  type="number"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.schoolOpened || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'schoolOpened', e.target.value)}
                                  placeholder="School Opened"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">No. of Time Present:</label>
                                <input
                                  type="number"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.present || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'present', e.target.value)}
                                  placeholder="Present"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">No. of Time Absent:</label>
                                <input
  type="number"
  className="border rounded px-2 py-1 w-full"
  value={overall?.[selectedStudent._id]?.absent ?? ''}
  onChange={e => handleOverallChange(selectedStudent._id, 'absent', e.target.value)}
  placeholder="Absent"
/>
                              </div>
                              <div>
                                <label className="font-semibold">Total Score:</label>
                                <input
                                  type="number"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.totalScore || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'totalScore', e.target.value)}
                                  placeholder="Total Score"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Project Defence:</label>
                                <input
                                  type="text"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.projectDefence || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'projectDefence', e.target.value)}
                                  placeholder="Project Defence"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Over-all Score:</label>
                                <input
                                  type="number"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.overallScore || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'overallScore', e.target.value)}
                                  placeholder="Over-all Score"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Average:</label>
                                <input
                                  type="number"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.average || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'average', e.target.value)}
                                  placeholder="Average"
                                />
                              </div>
                              <div>
                                <label className="font-semibold">Next Term Begins:</label>
                                <input
                                  type="text"
                                  className="border rounded px-2 py-1 w-full"
                                  value={overall?.[selectedStudent._id]?.nextTerm || ''}
                                  onChange={e => handleOverallChange(selectedStudent._id, 'nextTerm', e.target.value)}
                                  placeholder="Next Term Begins"
                                />
                              </div>
                            </div>
                            <div className="flex gap-4 mt-4">
                                {/* 2. In your Save button handler, replace the payload logic with this: */}
                                <Button
                                  variant="secondary"
                                  type="button"
                                  disabled={isPushed && !isChanged}
                                  className="w-full"
                                  onClick={async () => {
                                    setSuccessMsg('');
                                    setErrorMsg('');
                                    setIsSubmitting(true);
                                    const payload = buildSaveAllPayload(selectedStudent._id);
                                    try {
                                      const res = await fetch(`${API_BASE_URL}/api/results/save-all`, {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify(payload),
                                      });
                                      const data = await res.json();
                                      if (data.success) {
                                        setSuccessMsg('All results saved to backend.');
                                      } else {
                                        setErrorMsg('Failed to save result. Please try again.');
                                      }
                                    } catch (err) {
                                      setErrorMsg('Failed to save result. Please try again.');
                                    }
                                    setIsSubmitting(false);
                                  }}
                                >
                                  <Icon name="Save" size={16} className="mr-2" />
                                  {isPushed && !isChanged ? "Saved (Pushed)" : "Save"}
                                </Button>
                                <Button
                                    variant="primary"
                                    type="submit"
                                    disabled={isPushed && !isChanged}
                                    className="w-full"
                                >
                                  {isSubmitting ? (
                                    <>
                                      <TbFidgetSpinner className="animate-spin mr-2" />
                                      Saving...
                                    </>
                                  ) : (
                                    <>
                                      <Icon name="Send" size={16} className="mr-2" />
                                      {isPushed && !isChanged ? "Pushed" : "Save & Push Result"}
                                    </>
                                  )}
                                </Button>
                            </div>
                        </form>
                    </div>
                </div>
                <ConfirmationModal
                  show={showIndividualPushConfirm}
                  loading={isSubmitting}
                  message="Are you sure you want to push this student's result?"
                  onCancel={() => {
                    setShowIndividualPushConfirm(false);
                    setPendingIndividualPushId(null);
                  }}
                  onConfirm={async () => {
                    setShowIndividualPushConfirm(false);
                    if (pendingIndividualPushId) {
                      await handlePushIndividualResult(pendingIndividualPushId);
                      setPendingIndividualPushId(null);
                    }
                  }}
                />
            </main>
        );
    }

    // Otherwise, show the student list
    const allPushed = students.length > 0 && students.every(s => pushedMap[s._id]);
    const hasAnyEmptyStudent = getStudentsWithEmptyResults().length > 0;

    return (
        <>
            <RoleBasedHeader
                userRole="teacher"
                userName={teacherData?.name || 'Teacher'}
                isCollapsed={false}
            />
            <main className="pt-20 pb-10 bg-background min-h-screen">
                <div className="max-w-5xl mx-auto px-2 sm:px-4">
                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div>
                            <h1 className="font-heading text-3xl font-bold text-primary mb-2">
                                Result Portal - {terms.find(t => t.value === selectedTerm)?.label}
                            </h1>
                            <p className="text-muted-foreground text-lg">
                                Click a student to enter their result.
                            </p>
                        </div>
                        <Button
                          variant="primary"
                          className="w-full sm:w-auto"
                          disabled={
                            isSubmitting ||
                            students.length === 0 ||
                            allPushed ||
                            hasAnyEmptyStudent
                          }
                          onClick={() => setShowGeneralPushConfirm(true)}
                        >
                          {isSubmitting ? (
                            <>
                              <TbFidgetSpinner className="animate-spin mr-2" />
                              Pushing All...
                            </>
                          ) : allPushed ? (
                            <>
                              <Icon name="CheckCircle" size={16} className="mr-2" />
                              All Pushed
                            </>
                          ) : hasAnyEmptyStudent ? (
                            <>
                              <Icon name="AlertCircle" size={16} className="mr-2" />
                              Complete All Results First
                            </>
                          ) : (
                            <>
                              <Icon name="Send" size={16} className="mr-2" />
                              Push All Results
                            </>
                          )}
                        </Button>
                    </div>
                    {isLoadingStudents ? (
                        <div className="flex flex-col items-center justify-center py-12">
                            <TbFidgetSpinner className="animate-spin text-5xl text-primary mb-2" />
                            <span className="text-primary font-medium">Loading students...</span>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                            {students.map(student => (
                                <div
                                    key={student._id}
                                    className="bg-card border border-border rounded-lg shadow p-4 flex flex-col items-center cursor-pointer hover:bg-primary/10 transition"
                                    onClick={() => setSelectedStudent(student)}
                                >
                                    <span className="w-12 h-12 rounded-full bg-primary flex items-center justify-center text-xl font-bold text-white border-2 border-primary shadow mb-2">
                                        {student.name ? student.name.charAt(0).toUpperCase() : "S"}
                                    </span>
                                    <span className="font-body font-medium text-foreground">{student.name}</span>
                                    <span className="text-xs text-muted-foreground">{student.className}</span>
                                    {pushedMap[student._id] && (
                                      <span className="mt-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-300">
                                        <Icon name="CheckCircle" size={14} className="mr-1" /> Pushed
                                      </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            {(successMsg || errorMsg) && (
              <div
                className={`fixed bottom-6 left-1/2 transform -translate-x-1/2 px-4 py-2 rounded-lg font-medium text-center shadow-lg z-50 transition-all ${
                  successMsg
                    ? 'bg-green-100 text-green-700 border border-green-300'
                    : 'bg-red-100 text-red-700 border border-red-300'
                }`}
                style={{ minWidth: '280px', maxWidth: '90vw' }}
              >
                {successMsg || errorMsg}
                <button
                  className="ml-2 text-xs text-gray-500 hover:text-gray-700"
                  onClick={() => {
                    setSuccessMsg('');
                    setErrorMsg('');
                  }}
                  aria-label="Dismiss"
                >
                  &times;
                </button>
              </div>
            )}
            <ConfirmationModal
              show={showGeneralPushConfirm}
              loading={isSubmitting}
              message="Are you sure you want to push results for all students in this class?"
              onCancel={() => setShowGeneralPushConfirm(false)}
              onConfirm={async () => {
                setShowGeneralPushConfirm(false);
                await handleSubmitResults();
              }}
            />
        </>
    );
};

export default ResultPortal;